import { match } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import {
  CrewClaim,
  crewClaimContext,
} from '~/app/features/crew/crewClaimMiddleware';
import {
  CrewOrder,
  crewOrderContext,
} from '~/app/features/order-middleware/orderMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import api from '~/utils/api';
import {
  isCreditClaim,
  isLineItemInClaims,
  pendingClaimLineItemToCalculatedLineItem,
} from '~/utils/claimLineItem';
import { computeCost, sumBy } from '~/utils/compute';
import { ClaimType } from '~/utils/constants';
import {
  isItemReturnEligible,
  isItemWarrantyEligible,
  isWarrantyClaimAllowedOutsideWindow,
} from '~/utils/order';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { claimAddressSchema, draftClaimLineItemSchema } from '~/utils/schemas';
import { getClaimTypeProgramName } from '~/utils/theme';

type AppStore = Parameters<Parameters<typeof makeAction>[0]>[1];

const sumPendingClaimQuantities = (
  pendingLineItemClaims: CrewClaim['claimLineItems'],
) =>
  pendingLineItemClaims.reduce((sums, { lineItem: { id }, quantity }) => {
    const currentSum = sums.get(id) ?? 0;
    sums.set(id, currentSum + quantity);
    return sums;
  }, new Map<number, number>());

const updateCurrentQuantity = (
  pendingLineItemClaims: CrewClaim['claimLineItems'],
) => {
  const pendingClaimQuantities = sumPendingClaimQuantities(
    pendingLineItemClaims,
  );

  return (item: CrewOrder['lineItems'][number]) => {
    const { currentQuantity, id } = item;
    const pendingClaimQuantity = pendingClaimQuantities.get(id) ?? 0;

    return {
      ...item,
      currentQuantity: currentQuantity - pendingClaimQuantity,
    };
  };
};

const createEligibilityChecker = (options: {
  areReturnsEnabled: boolean;
  claimType: ClaimType;
}) =>
  options.claimType === ClaimType.warranty
    ? isItemWarrantyEligible
    : isItemReturnEligible(options.areReturnsEnabled);

// customer INFO hasn't been set yet, so we need to check if we can use the address or not
const parseAddress = (order: CrewOrder) => {
  const address = claimAddressSchema.safeParse(order.shippingAddress);
  if (!address.success) return null;
  return address.data;
};

export const { loader, useLoaderData } = makeLoader(
  async ({ context }, appStore) => {
    const settings = context.get(settingsContext);
    const crewClaim = context.get(crewClaimContext);
    const order = context.get(crewOrderContext);

    const {
      return: { isEnabled: areReturnsEnabled },
    } = settings;

    const { claimLineItems, claimType } = crewClaim;

    const lineItems = order.lineItems.map(
      updateCurrentQuantity(claimLineItems),
    );

    const isEligible = createEligibilityChecker({
      areReturnsEnabled,
      claimType,
    });

    const eligibleLineItems = lineItems.filter(isEligible);

    // when a selectable item is claimable past the window, don't advertise how long the warranty runs
    const isOutsideWindowOffer =
      claimType === ClaimType.warranty &&
      eligibleLineItems.some(isWarrantyClaimAllowedOutsideWindow);

    const lastActionableDate = match({ claimType, isOutsideWindowOffer })
      .with(
        { claimType: ClaimType.warranty, isOutsideWindowOffer: true },
        () => undefined,
      )
      .with({ claimType: ClaimType.warranty }, () => order.warrantyUntil)
      .otherwise(() => order.returnUntil);

    const claimTypeProgramName = getClaimTypeProgramName(
      settings.theme,
      claimType,
    );

    const hasCreditClaimsWithValue = sumBy(computeCost)(
      claimLineItems.filter(isCreditClaim),
    );

    const address = parseAddress(order);

    // we should run the claim line item review hook if shouldCheckShopNow is true or if we have shippingAddress
    const shouldCheckShopNow =
      claimType === ClaimType.return &&
      hasCreditClaimsWithValue > 0 &&
      claimLineItems.length > 0;

    const shouldPrequoteShipping =
      claimType === ClaimType.return && address && claimLineItems.length > 0;

    const { offerShopNow } =
      shouldCheckShopNow || shouldPrequoteShipping
        ? await api.calculateClaimLineItemReview({
            params: { storefrontId: settings.storefrontId },
            body: {
              id: order.id,
              claimType,
              email: order.email,
              calculatedLineItems: claimLineItems.map((item) => ({
                ...pendingClaimLineItemToCalculatedLineItem(item),
                grams: item.grams * item.quantity,
              })),
              ...(address && {
                address,
              }),
            },
          })
        : { offerShopNow: undefined };

    appStore.dispatch(appStore.actions.crewClaim.setOfferShopNow(offerShopNow));

    return {
      lastActionableDate,
      claimType,
      lineItems: eligibleLineItems,
      pendingClaims: claimLineItems,
      ineligibleItems: lineItems.filter(
        (item) =>
          !isEligible(item) && !isLineItemInClaims(item.id, claimLineItems),
      ),
      claimTypeProgramName,
      isOutsideWindowOffer,
      shopNowOffered: !!offerShopNow,
    };
  },
);

//
// Action
//

export const FormKeys = {
  claimId: 'claimId',
  lineItem: 'lineItem',
  claimType: 'claimType',
} as const;

const deleteSchema = zfd.formData({
  [FormKeys.claimId]: zfd.text(),
});

const handleDelete = (formData: FormData, appStore: AppStore) => {
  const { claimId } = deleteSchema.parse(formData);
  appStore.dispatch(appStore.actions.crewClaim.removeClaimLineItem(claimId));

  return {
    ok: true,
    message: 'Item removed',
  };
};

const postSchema = zfd.formData({
  [FormKeys.claimType]: zfd.text(z.enum(ClaimType)),
  [FormKeys.lineItem]: zfd.json(draftClaimLineItemSchema.shape.lineItem),
});

const handlePost = (formData: FormData, appStore: AppStore) => {
  const result = postSchema.safeParse(formData);

  if (!result.success) {
    throw new Error('Malformed form data', {
      cause: {
        error: result.error,
        entries: Array.from(formData.entries()),
      },
    });
  }

  const { lineItem, claimType } = result.data;

  const id = Math.random().toString(36).slice(2);

  // an expired item is only selectable when a rule allows claims past the window
  const wasOutsideEligibilityWindow =
    claimType === ClaimType.warranty &&
    isWarrantyClaimAllowedOutsideWindow(lineItem);

  appStore.dispatch(
    appStore.actions.draftClaimLineItem.startNewClaim({
      id,
      lineItem,
      quantity: 1,
      unitPrice: lineItem.unitPrice,
      unitTax: lineItem.unitTax,
      grams: lineItem.grams,
      customFields: {},
      comments: null,
      originalStoreOrderLineItemId: lineItem.id,
      wasOutsideEligibilityWindow,
    }),
  );

  return redirect(
    wasOutsideEligibilityWindow ? `${id}/warranty-window` : `${id}`,
  );
};

export const { action, useActionResult } = makeAction(
  ({ formData, request }, appStore) =>
    match(request.method)
      .with('DELETE', () => handleDelete(formData, appStore))
      .with('POST', () => handlePost(formData, appStore))
      .otherwise(() => {
        throw new Error('Invalid request method', {
          cause: { method: request.method },
        });
      }),
);
